import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import NewSelect from "../Inputs/NewSelect";
import NewText from "../Inputs/NewText";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper";
import "../styles/form-container.css";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow";
import "./styles/generalinfo.css";

// Slide 1: General Information
const GeneralInfo = ({ setSlideStatus }) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const {
    slide1,
    setSlide1,
    setIsLoading,
    requestStatus,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
  } = useContext(FormContext);

  const { projectId, clientId } = useContext(DashboardContext);

  // - On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide1);
  }, []);

  // Update the slide state
  const slide1Mutation = useSetSlide(
    clientId,
    projectId,
    1,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate invalid inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "1",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      <h2 className="question-section">Project Name & Type</h2>
      <p className="important-text">
        Please provide the project name, as stated or wanted on the NOI, and the
        project type.
      </p>
      <ResponsiveRow type="general">
        <NewText
          idProp={"projectName"}
          label="Project Name"
          state={slide1}
          setState={setSlide1}
          val={slide1.projectName}
          req={slide1.projectName === ""}
          setIsLoading={setIsLoading}
          type="project"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("projectName", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("projectName", disabledInputs) === false
              ? getInputMessage("projectName", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("projectName", invalidInputsData, "1")}
        />
        <NewSelect
          idProp={"projectType"}
          label="Project Type"
          state={slide1}
          setState={setSlide1}
          val={slide1.projectType}
          req={slide1.projectType === ""}
          setIsLoading={setIsLoading}
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("projectType", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("projectType", disabledInputs) === false
              ? getInputMessage("projectType", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("projectType", invalidInputsData, "1")}
          type="projectType"
        />
        {(slide1.projectType === "residential" ||
          slide1.projectType === "development & residential") && (
          <NewSelect
            idProp={"residenceType"}
            label="Residence Type"
            state={slide1}
            setState={setSlide1}
            val={slide1.residenceType}
            req={slide1.residenceType === ""}
            setIsLoading={setIsLoading}
            debouncedSave={projectId ? slide1Mutation : undefined}
            disabledInput={disabledInputCheck("residenceType", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("residenceType", disabledInputs) === false
                ? getInputMessage("residenceType", invalidInputsData, "1")
                : ""
            }
            invalidData={getInvalidData(
              "residenceType",
              invalidInputsData,
              "1",
            )}
            type="unitType"
          />
        )}
      </ResponsiveRow>
      {slide1.projectType === "residential" && (
        <>
          <h2 className="question-section">
            Previous Owner/Developer Information
          </h2>
          <ResponsiveRow type="center-gap-margin">
            <NewText
              idProp={"landDeveloper"}
              label="Previous Owner/Developer"
              state={slide1}
              setState={setSlide1}
              val={slide1.landDeveloper}
              setIsLoading={setIsLoading}
              req={slide1.landDeveloper === ""}
              type="company"
              debouncedSave={projectId ? slide1Mutation : undefined}
              disabledInput={disabledInputCheck(
                "landDeveloper",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("landDeveloper", disabledInputs) === false
                  ? getInputMessage("landDeveloper", invalidInputsData, "1")
                  : ""
              }
              invalidData={getInvalidData(
                "landDeveloper",
                invalidInputsData,
                "1",
              )}
            />
            <NewText
              idProp={"csw"}
              label="Existing CSW"
              state={slide1}
              setState={setSlide1}
              val={slide1.csw}
              setIsLoading={setIsLoading}
              req={slide1.csw === ""}
              type="number"
              debouncedSave={projectId ? slide1Mutation : undefined}
              disabledInput={disabledInputCheck("csw", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("csw", disabledInputs) === false
                  ? getInputMessage("csw", invalidInputsData, "1")
                  : ""
              }
              invalidData={getInvalidData("csw", invalidInputsData, "1")}
            />
          </ResponsiveRow>
        </>
      )}
      <h2 className="question-section">Project Size</h2>
      <ResponsiveRow type={"center-margin-gap"}>
        {(slide1.projectType === "residential" ||
          slide1.projectType === "commercial" ||
          slide1.projectType === "development & commercial" ||
          slide1.projectType === "development & residential") && (
          <NewText
            idProp={"totalBldLots"}
            label={
              slide1.residenceType === "S"
                ? "Total Homesites"
                : "Multi-Family Buildings"
            }
            state={slide1}
            setState={setSlide1}
            val={slide1.totalBldLots}
            setIsLoading={setIsLoading}
            req={slide1.totalBldLots === ""}
            type="lots"
            debouncedSave={projectId ? slide1Mutation : undefined}
            disabledInput={disabledInputCheck("totalBldLots", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("totalBldLots", disabledInputs) === false
                ? getInputMessage("totalBldLots", invalidInputsData, "1")
                : ""
            }
            invalidData={getInvalidData("totalBldLots", invalidInputsData, "1")}
          />
        )}
        {(slide1.residenceType === "M" || slide1.residenceType === "B") && (
          <NewText
            idProp={"totalUnits"}
            label={`${slide1.residenceType === "M" ? "Total Dwelling Units" : "Single-Family Homes"}`}
            state={slide1}
            setState={setSlide1}
            val={slide1.totalUnits}
            setIsLoading={setIsLoading}
            req={slide1.totalUnits === ""}
            type="lots"
            debouncedSave={projectId ? slide1Mutation : undefined}
            disabledInput={disabledInputCheck("totalUnits", disabledInputs)}
            disabledInputMessage={
              disabledInputCheck("totalUnits", disabledInputs) === false
                ? getInputMessage("totalUnits", invalidInputsData, "1")
                : ""
            }
            invalidData={getInvalidData("totalUnits", invalidInputsData, "1")}
          />
        )}
        <NewText
          idProp={"totAcr"}
          label="Total Acreage"
          state={slide1}
          setState={setSlide1}
          val={slide1.totAcr}
          setIsLoading={setIsLoading}
          req={slide1.totAcr === ""}
          type="acreage"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("totAcr", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("totAcr", disabledInputs) === false
              ? getInputMessage("totAcr", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("totAcr", invalidInputsData, "1")}
        />
        <NewText
          idProp={"distAcr"}
          label="Disturbed Acreage"
          state={slide1}
          setState={setSlide1}
          val={slide1.distAcr}
          setIsLoading={setIsLoading}
          req={slide1.distAcr === ""}
          type="acreage"
          debouncedSave={projectId ? slide1Mutation : undefined}
          disabledInput={disabledInputCheck("distAcr", disabledInputs)}
          disabledInputMessage={
            disabledInputCheck("distAcr", disabledInputs) === false
              ? getInputMessage("distAcr", invalidInputsData, "1")
              : ""
          }
          invalidData={getInvalidData("distAcr", invalidInputsData, "1")}
        />
      </ResponsiveRow>
    </>
  );
};

export default GeneralInfo;
